import React, { useState, useEffect } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotification } from './context/NotificationContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0c0d12',
      paper: '#14171f',
    },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: {
      main: '#6aa9ff',
    },
    success: {
      main: '#5ad27a',
    },
    error: {
      main: '#ff6b6b',
    },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.02)',
    color: '#e8e9ee',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#6aa9ff' },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(232,233,238,0.7)',
  },
  '& .MuiFormHelperText-root': {
    color: 'rgba(255,107,107,0.9)',
  },
};

const RouteConfigurations = () => {
  const { showNotification } = useNotification();
  const [routes, setRoutes] = useState({});
  const [routeName, setRouteName] = useState('');
  const [keywords, setKeywords] = useState('');
  const [allowedSenders, setAllowedSenders] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const correctPassword = '!Cfaklapo109?';

  useEffect(() => {
    fetchRoutes();
  }, []);

  useEffect(() => {
    document.title = 'Bridge Manager';
  }, []);

  const fetchRoutes = async () => {
    try {
      const response = await fetch('https://frek.cc/proxy/38.54.86.24:5005/update-routes', {
        method: 'GET',
      });
      const data = await response.json();
      setRoutes(data || {});
    } catch (error) {
      console.error('Error fetching routes:', error);
      showSnackbar('Error fetching routes.', 'error');
    }
  };

  const handleOpenDialog = (editMode = false, route = '', routeKeywords = '', senders = '') => {
    setIsEditMode(editMode);
    setRouteName(route);
    setKeywords(routeKeywords);
    setAllowedSenders(senders);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRouteName('');
    setKeywords('');
    setAllowedSenders('');
  };

  const handleEdit = (selectedRouteName, routeKeywords, routeAllowedSenders) => {
    handleOpenDialog(
      true,
      selectedRouteName,
      routeKeywords.join(', '),
      routeAllowedSenders.join(', ')
    );
  };

  const handleDelete = (selectedRouteName) => {
    setPendingAction(() => () => performDelete(selectedRouteName));
    setOpenConfirmation(true);
  };

  const performDelete = async (selectedRouteName) => {
    try {
      await fetch('https://frek.cc/proxy/38.54.86.24:5005/update-routes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          route_name: selectedRouteName,
          action: 'delete',
        }),
      });
      fetchRoutes();
      showSnackbar('Route deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting route:', error);
      showSnackbar('Error deleting route.', 'error');
    }
  };

  const handleSave = async () => {
    const keywordsArray = keywords
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean);
    const allowedSendersArray = allowedSenders
      .split(',')
      .map((sender) => sender.trim())
      .filter(Boolean);

    try {
      await fetch('https://frek.cc/proxy/38.54.86.24:5005/update-routes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          route_name: routeName,
          keywords: keywordsArray,
          allowed_senders: allowedSendersArray,
        }),
      });
      fetchRoutes();
      handleCloseDialog();
      showSnackbar(isEditMode ? 'Route updated successfully!' : 'Route added successfully!', 'success');
    } catch (error) {
      console.error('Error saving route:', error);
      showSnackbar('Error saving route.', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    showNotification(message, severity);
  };

  const handleOpenConfirmation = (action) => {
    setPendingAction(() => action);
    setOpenConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
    setPassword('');
    setPasswordError(false);
  };

  const handleConfirmAction = () => {
    if (password === correctPassword) {
      if (pendingAction) {
        pendingAction();
      }
      handleCloseConfirmation();
    } else {
      setPasswordError(true);
      showSnackbar('Incorrect password. Please try again.', 'error');
    }
  };

  const routeCount = Object.keys(routes).length;

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Bridge Manager
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                      Manage route keywords and allowed senders.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      startIcon={<RefreshIcon />}
                      variant="contained"
                      size="small"
                      onClick={fetchRoutes}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={() => handleOpenDialog(false)}
                    >
                      Add New Route
                    </Button>
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={`Routes ${routeCount}`}
                    sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              {routeCount === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.55)' }}>
                  No routes found.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: '76vh' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {['Route', 'Keywords', 'Allowed Senders', 'Edit', 'Delete'].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontWeight: 700,
                              color: '#d9e6ff',
                              backgroundColor: 'rgba(30, 35, 52, 0.92)',
                              borderBottom: '1px solid rgba(255,255,255,0.12)',
                              whiteSpace: 'nowrap',
                              textAlign: 'center',
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(routes).map(([routeNameKey, config]) => (
                        <TableRow
                          key={routeNameKey}
                          sx={{
                            '& td': { borderColor: 'rgba(255,255,255,0.08)' },
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                          }}
                        >
                          <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>{routeNameKey}</TableCell>
                          <TableCell sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.78)' }}>
                            {(config.keywords || []).join(', ')}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.78)' }}>
                            {(config.allowed_senders || []).join(', ')}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleEdit(routeNameKey, config.keywords || [], config.allowed_senders || [])
                              }
                            >
                              Edit
                            </Button>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleDelete(routeNameKey)}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Stack>
        </Container>

        <Dialog
          fullWidth
          maxWidth="md"
          open={openDialog}
          onClose={handleCloseDialog}
          PaperProps={{
            sx: {
              background: 'rgba(18, 18, 22, 0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#e8e9ee',
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>{isEditMode ? 'Edit Route' : 'Add Route'}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <TextField
                label="Route Name"
                variant="outlined"
                fullWidth
                value={routeName}
                onChange={(e) => setRouteName(e.target.value)}
                sx={fieldSx}
              />
              <TextField
                label="Keywords (comma-separated)"
                variant="outlined"
                fullWidth
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                multiline
                minRows={3}
                sx={fieldSx}
              />
              <TextField
                label="Allowed Senders (comma-separated)"
                variant="outlined"
                fullWidth
                value={allowedSenders}
                onChange={(e) => setAllowedSenders(e.target.value)}
                multiline
                minRows={3}
                sx={fieldSx}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={() => handleOpenConfirmation(handleSave)}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openConfirmation}
          onClose={handleCloseConfirmation}
          PaperProps={{
            sx: {
              background: 'rgba(18, 18, 22, 0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#e8e9ee',
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              helperText={passwordError ? 'Incorrect password.' : ''}
              sx={fieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseConfirmation}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirmAction}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default RouteConfigurations;
